import React, { HTMLAttributes } from 'react';
import cn from 'classnames';

type TypographyTag =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'label'
  | 'pre';

export type TypographyColor =
  | 'default'
  | 'secondary'
  | 'disabled'
  | 'knockout'
  | 'primary'
  | 'danger'
  | 'success'
  | 'warning';

export type TypographyVariant =
  | 'headline-large'
  | 'headline-medium'
  | 'headline-small'
  | 'title-large'
  | 'title-medium'
  | 'title-small'
  | 'paragraph-large'
  | 'paragraph-medium'
  | 'paragraph-small'
  | 'code'
  | 'code-small';

interface TypographyProps extends HTMLAttributes<any> {
  /**
   * The tag the Typography component will render as.
   */
  as?: TypographyTag;

  /**
   * If true, the text will be displayed in bold.
   * @default false
   */
  bold?: boolean;

  /**
   * The content of the component.
   */
  children: React.ReactNode;

  /**
   * An optional color to pass to the typography component.
   * @default "default"
   */
  color?: TypographyColor;

  /**
   * @ignore
   * Classes applied to the root element.
   */
  className?: string;

  /**
   * If true, the text will be displayed in italics.
   * @default false
   */
  italicized?: boolean;

  /**
   * The variant to use for the component.
   * See {@link https://www.figma.com/file/wI6uUtujPkJqQysZt2aMdS/Foundation?node-id=2744-1375&t=CoPmLm79oPBeTlE0-0 Figma} for differences between the variants.
   */
  variant: TypographyVariant;

  /**
   * If true, the text will be underlined.
   * @default false
   */
  underlined?: boolean;
}

export const variantsMapping: {
  [variant in TypographyVariant]: TypographyTag;
} = {
  'headline-large': 'h1',
  'headline-medium': 'h2',
  'headline-small': 'h2',
  'title-large': 'h2',
  'title-medium': 'h3',
  'title-small': 'h4',
  'paragraph-large': 'p',
  'paragraph-medium': 'p',
  'paragraph-small': 'p',
  'code': 'pre',
  'code-small': 'pre',
};

const Typography = ({
  as,
  bold = false,
  children,
  className,
  color = 'default',
  italicized = false,
  variant,
  underlined,
  ...rest
}: TypographyProps) => {
  const Component = as ?? variantsMapping[variant];

  return (
    <Component
      className={cn(
        variant,
        {
          bold: bold,
          italicized: italicized,
          underlined: underlined,
        },
        `${variant}--${color}`,
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  );
};

Typography.displayName = 'Typography';

export default Typography;
